import React, { useRef } from "react";
import { motion, useScroll, useTransform, type MotionProps } from "framer-motion";
import Ripple from "../ui/Ripple";

const MotionH1 = motion.h1 as unknown as React.FC<React.ComponentPropsWithoutRef<'h1'> & MotionProps & { className?: string; style?: React.CSSProperties }>;
const MotionDiv = motion.div as unknown as React.FC<React.ComponentPropsWithoutRef<'div'> & MotionProps & { className?: string; style?: React.CSSProperties }>;

const Hero: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Track scroll progress within the hero section
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  // Parallax transforms - elements move at different speeds
  const titleY = useTransform(scrollYProgress, [0, 1], [0, 100]);
  const subtitleY = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const orbLeftY = useTransform(scrollYProgress, [0, 1], [0, -50]);
  const orbRightY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  // Fade effects based on scroll
  const contentOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const rippleOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const scrollIndicatorOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0]);

  return (
    <div ref={containerRef} className="relative w-full min-h-screen flex flex-col items-center justify-center text-center px-4 py-20">
      {/* ENHANCED RIPPLE - Background with fade on scroll */}
      <motion.div style={{ opacity: rippleOpacity }}>
        <Ripple mainCircleSize={300} numCircles={9} mainCircleOpacity={0.4} />
      </motion.div>

      {/* Subtle gradient orbs with parallax */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          style={{ y: orbLeftY }}
          className="absolute top-1/4 -left-20 w-96 h-96 bg-matcha-200/20 rounded-full blur-3xl"
        />
        <motion.div
          style={{ y: orbRightY }}
          className="absolute bottom-1/4 -right-20 w-80 h-80 bg-matcha-300/15 rounded-full blur-3xl"
        />
      </div>

      <motion.div style={{ opacity: contentOpacity }} className="z-10 space-y-8">
        {/* Giant stacked name with parallax */}
        <MotionH1
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          style={{ y: titleY }}
          className="text-7xl md:text-8xl lg:text-9xl font-black tracking-tighter text-matcha-600 drop-shadow-sm"
        >
          ANTHONY
          <br />
          <span className="text-matcha-600">ABID</span>
        </MotionH1>

        {/* Title + code snippet with parallax */}
        <MotionDiv
          initial={{ opacity: 0, y: 20, filter: "blur(8px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 0.8, delay: 0.2 }}
          style={{ y: subtitleY }}
          className="space-y-3"
        >
          <p className="text-2xl md:text-3xl text-slate-800 font-bold">
            VP of Product Development
          </p>
          <div className="flex items-center justify-center gap-2 text-slate-500 font-mono text-sm">
            <span>function build()</span>
            <span className="text-matcha-500 font-bold">{"→"}</span>
            <span>return "impact"</span>
          </div>
        </MotionDiv>

        {/* Scroll indicator with animated line - fades out quickly on scroll */}
        <MotionDiv
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 1 }}
          style={{ opacity: scrollIndicatorOpacity }}
          className="pt-12"
        >
          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.3em] mb-4">
            SCROLL TO BEGIN
          </div>
          <div className="w-[2px] h-12 bg-slate-200 mx-auto overflow-hidden rounded-full">
            <div className="w-full h-full bg-matcha-500 animate-scroll-down" />
          </div>
        </MotionDiv>
      </motion.div>
    </div>
  );
};

export default Hero;
